/**
 * Texture preview generation utilities
 */

/**
 * Generate a preview canvas for a preset texture
 * @param {string} presetName - Preset name ('noise', 'gradient', 'checker', 'camera')
 * @param {number} size - Canvas size (default 64)
 * @returns {string} Data URL of the preview image
 */
export function generatePresetPreview(presetName, size = 64) {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');

  switch (presetName) {
    case 'noise':
      drawNoisePreview(ctx, size);
      break;
    case 'gradient':
      drawGradientPreview(ctx, size);
      break;
    case 'checker':
      drawCheckerPreview(ctx, size);
      break;
    case 'camera':
      drawCameraPreview(ctx, size);
      break;
    default:
      // Default: gray background
      ctx.fillStyle = '#808080';
      ctx.fillRect(0, 0, size, size);
  }

  return canvas.toDataURL();
}

/**
 * Draw noise preview
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} size
 */
function drawNoisePreview(ctx, size) {
  const imageData = ctx.createImageData(size, size);
  for (let i = 0; i < imageData.data.length; i += 4) {
    const value = Math.floor(Math.random() * 256);
    imageData.data[i] = value;
    imageData.data[i + 1] = value;
    imageData.data[i + 2] = value;
    imageData.data[i + 3] = 255;
  }
  ctx.putImageData(imageData, 0, 0);
}

/**
 * Draw gradient preview (left to right, black to white)
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} size
 */
function drawGradientPreview(ctx, size) {
  const gradient = ctx.createLinearGradient(0, 0, size, 0);
  gradient.addColorStop(0, '#000000');
  gradient.addColorStop(1, '#ffffff');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, size, size);
}

/**
 * Draw checker preview
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} size
 */
function drawCheckerPreview(ctx, size) {
  const checkerSize = size / 8;
  for (let y = 0; y < size; y += checkerSize) {
    for (let x = 0; x < size; x += checkerSize) {
      const isWhite = ((x / checkerSize) + (y / checkerSize)) % 2 === 0;
      ctx.fillStyle = isWhite ? '#ffffff' : '#000000';
      ctx.fillRect(x, y, checkerSize, checkerSize);
    }
  }
}

/**
 * Draw camera icon preview
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} size
 */
function drawCameraPreview(ctx, size) {
  const center = size / 2;
  const outerRadius = size / 4;
  const innerRadius = size / 8;

  ctx.fillStyle = '#333';
  ctx.fillRect(0, 0, size, size);

  ctx.fillStyle = '#4a9eff';
  ctx.beginPath();
  ctx.arc(center, center, outerRadius, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = '#fff';
  ctx.beginPath();
  ctx.arc(center, center, innerRadius, 0, Math.PI * 2);
  ctx.fill();
}
